import { z } from 'zod';

/**
 * Canonical aspect-ratio options for AI image generation. Providers map these
 * to their nearest supported output (Flux renders the exact ratio; OpenAI snaps
 * to its closest native bucket), and the display layer finishes the fit with
 * CSS object-cover — see RATIO_DIMS / openaiSizeFor in ai.service.ts.
 */
export const IMAGE_ASPECT_RATIOS = ['1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16'] as const;
export type ImageAspectRatio = (typeof IMAGE_ASPECT_RATIOS)[number];

// gpt-image-1's real quality scale (Nano Banana / Flux have no quality knob and
// ignore it). Replaces the DALL·E-3-era 'standard'/'hd'.
export const IMAGE_QUALITIES = ['auto', 'low', 'medium', 'high'] as const;
export type ImageQuality = (typeof IMAGE_QUALITIES)[number];

// Style "presets" are prompt-engineered (appended to the prompt), so they work
// on every prompt-driven model — gpt-image-1 AND Gemini Nano Banana — unlike
// DALL·E-3's `style: natural|vivid`, which neither of those models accepts.
export const IMAGE_STYLE_PRESETS = [
  'auto',
  'photographic',
  'illustration',
  'storybook',
  'flat_vector',
  'watercolor',
  'three_d',
  'cartoon',
  'line_art',
] as const;
export type ImageStylePreset = (typeof IMAGE_STYLE_PRESETS)[number];

// gpt-image-1 background control (transparent needs a PNG/WebP output). Other
// providers ignore it.
export const IMAGE_BACKGROUNDS = ['auto', 'opaque', 'transparent'] as const;
export type ImageBackground = (typeof IMAGE_BACKGROUNDS)[number];

// Legacy size tokens accepted from older clients/links, mapped to a ratio.
const LEGACY_SIZE_TO_RATIO: Record<string, ImageAspectRatio> = {
  square: '1:1',
  landscape: '3:2',
  portrait: '2:3',
};

export const generateImageInput = z.object({
  prompt: z.string().trim().min(1).max(2000),
  // Accept the new ratio tokens; gracefully coerce the three legacy size tokens.
  aspectRatio: z
    .union([z.enum(IMAGE_ASPECT_RATIOS), z.enum(['square', 'landscape', 'portrait'])])
    .transform((v): ImageAspectRatio =>
      (LEGACY_SIZE_TO_RATIO[v as string] ?? v) as ImageAspectRatio
    )
    .default('1:1'),
  // Coerce the old DALL·E-3 style tokens to "no preset".
  stylePreset: z
    .union([z.enum(IMAGE_STYLE_PRESETS), z.enum(['natural', 'vivid'])])
    .transform((v): ImageStylePreset =>
      (IMAGE_STYLE_PRESETS as readonly string[]).includes(v) ? (v as ImageStylePreset) : 'auto'
    )
    .default('auto'),
  // Coerce the old 'standard'/'hd' quality tokens to the new scale.
  quality: z
    .union([z.enum(IMAGE_QUALITIES), z.enum(['standard', 'hd'])])
    .transform((v): ImageQuality => (v === 'standard' ? 'medium' : v === 'hd' ? 'high' : v))
    .default('auto'),
  background: z.enum(IMAGE_BACKGROUNDS).default('auto'),
  configId: z.string().uuid().optional(),
  // Optional reference image (S3 key of a previously-generated image). When
  // supplied and the provider supports it (OpenAI gpt-image-1), generation uses
  // image-to-image (`images.edit`) so the output keeps the reference's identity
  // — used to chain a creature's evolution stages off the previous stage's art.
  referenceS3Key: z.string().trim().max(500).optional(),
  purpose: z
    .enum([
      'social_post',
      'course_thumbnail',
      'certificate_background',
      'banner',
      'achievement_card',
      'reading_illustration',
    ])
    .optional(),
});

export const buildImagePromptInput = z.object({
  purpose: z.enum([
    'social_post',
    'course_thumbnail',
    'certificate_background',
    'banner',
    'achievement_card',
    'reading_illustration',
  ]),
  contextData: z
    .record(z.string().max(100).trim(), z.string().max(500).trim())
    .refine((obj) => Object.keys(obj).length <= 20, 'Too many context fields'),
});

export const getImageUrlInput = z.object({
  s3Key: z.string().trim().max(500),
});

export type GenerateImageInput = z.infer<typeof generateImageInput>;
export type BuildImagePromptInput = z.infer<typeof buildImagePromptInput>;
